import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, storage } from '../../services/firebase';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { getUserById } from '../../utils/userUtils';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { formatFirestoreError, formatStorageError } from '../../utils/errorMessages';

export const useProfileEdit = () => {
  const [name, setName] = useState('');
  const [bio, setBio] = useState('');
  const [email, setEmail] = useState('');
  const [currentPhotoURL, setCurrentPhotoURL] = useState('');
  const [photoFile, setPhotoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Load current user profile info on mount
  useEffect(() => {
    const loadUserProfile = async () => {
      const u = auth.currentUser;
      if (!u) {
        navigate('/login');
        return;
      }

      try {
        setEmail(u.email || '');
        const userObj = await getUserById(u.uid);
        if (userObj) {
          setName(userObj.name || '');
          setBio(userObj.bio || '');
          setCurrentPhotoURL(userObj.profilePic || '');
        } else {
          // If user doc doesn't exist, create it
          const refDoc = doc(db, 'users', u.uid);
          await setDoc(refDoc, {
            name: u.displayName || '',
            email: u.email,
            bio: '',
            joinedAt: serverTimestamp(),
            profilePic: ''
          }, { merge: true });
          setName(u.displayName || '');
          setBio('');
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadUserProfile();
  }, [navigate]);

  // Handle profile save (name, bio, and optional new photo)
  const handleSave = async (e) => {
    e.preventDefault();
    const u = auth.currentUser;
    if (!u) return;

    try {
      setSaving(true);

      // Upload new profile photo if selected
      let photoURL;
      if (photoFile) {
        const ext = photoFile.name.split('.').pop() || 'jpg';
        const storageRef = ref(storage, `profilePics/${u.uid}.${ext}`);
        await uploadBytes(storageRef, photoFile);
        photoURL = await getDownloadURL(storageRef);
      }

      // Update Firestore user document
      const updates = {
        name: name.trim(),
        bio: bio.trim(),
        lastUpdatedAt: serverTimestamp(),
      };
      if (photoURL) updates.profilePic = photoURL;

      await updateDoc(doc(db, 'users', u.uid), updates);

      alert('Profile updated successfully.');
      navigate('/profile');
    } catch (err) {
      // Format error message based on error type
      let errorMessage;
      if (err.code?.startsWith('storage/')) {
        errorMessage = formatStorageError(err);
      } else {
        errorMessage = formatFirestoreError(err);
      }
      
      alert(`Failed to update profile: ${errorMessage}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  return {
    name,
    setName,
    bio,
    setBio,
    email,
    currentPhotoURL,
    photoFile,
    setPhotoFile,
    saving,
    loading,
    handleSave,
    handleCancel
  };
};